/**
 * NotificationsContext — React context for managing user notifications
 * with fetch, mark-as-read, and real-time WebSocket event handling.
 *
 * Requirements: 12.1, 12.2, 12.3, 12.4, 12.5
 */

import React, { createContext, useCallback, useContext, useReducer } from 'react';
import * as api from '../services/api-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface NotificationItem {
  readonly notificationId: string;
  readonly type: 'mention' | 'assignment' | 'due-date' | 'comment' | 'update';
  readonly message: string;
  readonly actorName: string;
  readonly read: boolean;
  readonly createdAt: string;
  readonly resourceType: 'board' | 'card';
  readonly resourceId: string;
  readonly boardId: string;
}

interface NotificationsState {
  readonly notifications: NotificationItem[];
  readonly unreadCount: number;
  readonly isLoading: boolean;
  readonly error: string | null;
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

type NotificationsAction =
  | { type: 'FETCH_NOTIFICATIONS_START' }
  | { type: 'FETCH_NOTIFICATIONS_SUCCESS'; payload: { notifications: NotificationItem[] } }
  | { type: 'FETCH_NOTIFICATIONS_ERROR'; payload: string }
  | { type: 'MARK_READ_OPTIMISTIC'; payload: { notificationId: string } }
  | { type: 'MARK_READ_REVERT'; payload: { notificationId: string } }
  | { type: 'MARK_ALL_READ_OPTIMISTIC' }
  | { type: 'MARK_ALL_READ_REVERT'; payload: { previousNotifications: NotificationItem[] } }
  | { type: 'NOTIFICATION_RECEIVED'; payload: NotificationItem };

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

const initialState: NotificationsState = {
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
};

function computeUnreadCount(notifications: NotificationItem[]): number {
  return notifications.filter((n) => !n.read).length;
}

function notificationsReducer(state: NotificationsState, action: NotificationsAction): NotificationsState {
  switch (action.type) {
    case 'FETCH_NOTIFICATIONS_START':
      return { ...state, isLoading: true, error: null };

    case 'FETCH_NOTIFICATIONS_SUCCESS': {
      const notifications = action.payload.notifications;
      return {
        ...state,
        isLoading: false,
        notifications,
        unreadCount: computeUnreadCount(notifications),
        error: null,
      };
    }

    case 'FETCH_NOTIFICATIONS_ERROR':
      return { ...state, isLoading: false, error: action.payload };

    case 'MARK_READ_OPTIMISTIC': {
      const updated = state.notifications.map((n) =>
        n.notificationId === action.payload.notificationId ? { ...n, read: true } : n,
      );
      return { ...state, notifications: updated, unreadCount: computeUnreadCount(updated) };
    }

    case 'MARK_READ_REVERT': {
      const reverted = state.notifications.map((n) =>
        n.notificationId === action.payload.notificationId ? { ...n, read: false } : n,
      );
      return { ...state, notifications: reverted, unreadCount: computeUnreadCount(reverted) };
    }

    case 'MARK_ALL_READ_OPTIMISTIC': {
      const allRead = state.notifications.map((n) => ({ ...n, read: true }));
      return { ...state, notifications: allRead, unreadCount: 0 };
    }

    case 'MARK_ALL_READ_REVERT': {
      const prev = action.payload.previousNotifications;
      return { ...state, notifications: prev, unreadCount: computeUnreadCount(prev) };
    }

    case 'NOTIFICATION_RECEIVED': {
      const prepended = [action.payload, ...state.notifications];
      return {
        ...state,
        notifications: prepended,
        unreadCount: computeUnreadCount(prepended),
      };
    }

    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

interface NotificationsContextValue {
  readonly state: NotificationsState;
  fetchNotifications: () => Promise<void>;
  markRead: (notificationId: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  handleWebSocketEvent: (event: Record<string, unknown>) => void;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

// ---------------------------------------------------------------------------
// API response types
// ---------------------------------------------------------------------------

interface NotificationsApiResponse {
  readonly data: NotificationItem[];
}

// ---------------------------------------------------------------------------
// Provider component
// ---------------------------------------------------------------------------

interface NotificationsProviderProps {
  readonly children: React.ReactNode;
  /** Skip initial fetch — useful for testing */
  readonly skipInitialFetch?: boolean;
}

export function NotificationsProvider({
  children,
  skipInitialFetch,
}: NotificationsProviderProps): React.ReactElement {
  const [state, dispatch] = useReducer(notificationsReducer, initialState);

  const fetchNotifications = useCallback(async (): Promise<void> => {
    dispatch({ type: 'FETCH_NOTIFICATIONS_START' });
    try {
      const response = await api.get<NotificationsApiResponse>('/v1/notifications');
      dispatch({
        type: 'FETCH_NOTIFICATIONS_SUCCESS',
        payload: { notifications: response.data },
      });
    } catch (error: unknown) {
      const message = extractErrorMessage(error);
      dispatch({ type: 'FETCH_NOTIFICATIONS_ERROR', payload: message });
    }
  }, []);

  const markRead = useCallback(async (notificationId: string): Promise<void> => {
    dispatch({ type: 'MARK_READ_OPTIMISTIC', payload: { notificationId } });
    try {
      await api.put(`/v1/notifications/${notificationId}`, { read: true });
    } catch {
      dispatch({ type: 'MARK_READ_REVERT', payload: { notificationId } });
    }
  }, []);

  const markAllRead = useCallback(async (): Promise<void> => {
    const previousNotifications = state.notifications;
    dispatch({ type: 'MARK_ALL_READ_OPTIMISTIC' });
    try {
      await api.put('/v1/notifications', { readAll: true });
    } catch {
      dispatch({ type: 'MARK_ALL_READ_REVERT', payload: { previousNotifications } });
    }
  }, [state.notifications]);

  const handleWebSocketEvent = useCallback((event: Record<string, unknown>): void => {
    if (event.eventType === 'NotificationCreated' && isNotificationItem(event.payload)) {
      dispatch({ type: 'NOTIFICATION_RECEIVED', payload: event.payload });
    }
  }, []);

  // Fetch on mount unless skipped
  React.useEffect(() => {
    if (!skipInitialFetch) {
      fetchNotifications();
    }
  }, [fetchNotifications, skipInitialFetch]);

  const value: NotificationsContextValue = {
    state,
    fetchNotifications,
    markRead,
    markAllRead,
    handleWebSocketEvent,
  };

  return (
    <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Access the notifications context. Must be used within a NotificationsProvider.
 */
export function useNotifications(): NotificationsContextValue {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
}

// ---------------------------------------------------------------------------
// Type guard
// ---------------------------------------------------------------------------

function isNotificationItem(value: unknown): value is NotificationItem {
  if (!value || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.notificationId === 'string' &&
    typeof obj.message === 'string' &&
    typeof obj.createdAt === 'string' &&
    typeof obj.read === 'boolean'
  );
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function extractErrorMessage(error: unknown): string {
  if (api.isClientError(error)) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

// Export reducer and types for testing
export { notificationsReducer, initialState };
export type { NotificationsState, NotificationsAction };
